/**
 * APT（高度プロファイルタスク、AXMER 15.21）のチャート。
 * 横軸＝タスク開始（電子マーク）からの経過時間 / 縦軸＝高度。
 *
 * 規定が定める空域を、そのまま帯として描く:
 *   内側帯A … 目標高度 ±innerBandFt。ここにいた時間は**2倍**
 *   外側帯B … ±innerBandFt〜±outerBandFt。**1倍**
 *   帯の外  … 数えない
 *
 * ★ ウィンドプロファイラ（ui/wind-profile.ts）と軸の意味が同じ（時間×高度）
 *   だが、あちらの横軸は「時刻」でこちらは「タスク開始からの経過時間」。
 *   目盛りのラベル（分）で必ず区別できるようにする。
 *
 * ★ ここに出る累積時間は**参考値**であって公式スコアではない（gv-core の
 *   airspace.ts 冒頭も参照）。画面上でも必ずそう分かる形で出すこと。
 *
 * DOM を触らない純粋関数にしてある（ui/wind-profile.ts と同じ流儀）。
 */

import type { AptTask } from '@gv4d/core';
import { colorForTerminal, type AltitudeStop } from '../map/layers.ts';
import { metersToFeet } from '../units.ts';

/** チャートに重ねる実測の航跡（機体1つぶん） */
export interface AptChartTrack {
  terminalId: string;
  label: string;
  /** 実測点。t は UTC epoch ms、alt は [m]（内部データの単位のまま渡す） */
  points: Array<{ t: number; alt: number }>;
}

export interface AptChartInput {
  task: AptTask;
  /** タスク開始（電子マーク）[UTC epoch ms]。未設定なら航跡を重ねられない */
  startMs: number | null;
  tracks: AptChartTrack[];
  /** 「いま」の時刻 [UTC epoch ms]。経過時間の縦線を出す。null なら出さない */
  nowMs: number | null;
  stops: AltitudeStop[];
  width: number;
  height: number;
}

/** 描画領域の余白。左は高度ラベル、下は経過時間ラベルのぶん広く取る */
const PAD = { top: 16, right: 16, bottom: 34, left: 54 };

/** 内側帯A（2倍）の色。濃いほうが有利な帯だと一目で分かるようにする */
const BAND_INNER_COLOR = '#3f7fbf';
/** 外側帯B（1倍）の色 */
const BAND_OUTER_COLOR = '#bcd7f2';
/** 目標プロファイルの中心線 */
const PROFILE_COLOR = '#14335c';

/** プロファイルが定義されている経過時間の範囲 [秒] */
export function aptSpanSec(task: AptTask): { fromSec: number; toSec: number } {
  if (task.profile.length === 0) return { fromSec: 0, toSec: 0 };
  return {
    fromSec: task.profile[0].tSec,
    toSec: task.profile[task.profile.length - 1].tSec,
  };
}

/**
 * 表示する高度の範囲 [ft]。
 * 帯の上下が必ず入るようにし、実測が帯から外れていてもその点まで見せる
 * （外れていることが読めないと、戻す判断ができない）。
 */
function altRangeFt(input: AptChartInput): { minFt: number; maxFt: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const p of input.task.profile) {
    if (p.altFt - input.task.outerBandFt < min) min = p.altFt - input.task.outerBandFt;
    if (p.altFt + input.task.outerBandFt > max) max = p.altFt + input.task.outerBandFt;
  }
  if (input.startMs != null) {
    const { fromSec, toSec } = aptSpanSec(input.task);
    for (const tr of input.tracks) {
      for (const pt of tr.points) {
        const sec = (pt.t - input.startMs) / 1000;
        if (sec < fromSec || sec > toSec) continue;
        const ft = metersToFeet(pt.alt);
        if (ft < min) min = ft;
        if (ft > max) max = ft;
      }
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return { minFt: 0, maxFt: 3000 };
  }
  const margin = Math.max(50, (max - min) * 0.1);
  return { minFt: min - margin, maxFt: max + margin };
}

export function buildAptChartSvg(input: AptChartInput): string {
  const { task, startMs, tracks, nowMs, stops, width, height } = input;

  const plotW = Math.max(1, width - PAD.left - PAD.right);
  const plotH = Math.max(1, height - PAD.top - PAD.bottom);
  const { fromSec, toSec } = aptSpanSec(task);
  const spanSec = Math.max(1, toSec - fromSec);
  const { minFt, maxFt } = altRangeFt(input);
  const rangeFt = Math.max(1, maxFt - minFt);

  const xOf = (sec: number) => PAD.left + ((sec - fromSec) / spanSec) * plotW;
  const yOf = (ft: number) => PAD.top + plotH - ((ft - minFt) / rangeFt) * plotH;
  const f = (n: number) => n.toFixed(1);

  const parts: string[] = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" ` +
      `width="100%" height="100%" role="img" ` +
      `aria-label="APT 高度プロファイルタスク（横軸＝経過時間／縦軸＝高度）">`,
  );

  if (task.profile.length === 0) {
    parts.push(
      `<text x="${f(width / 2)}" y="${f(height / 2)}" text-anchor="middle" ` +
        `font-size="12" fill="currentColor" fill-opacity="0.55">` +
        `高度プロファイルが未設定です</text></svg>`,
    );
    return parts.join('');
  }

  // --- 高度の目盛り（地図の凡例・他の画面と同じ段・同じ色） ---
  for (const stop of stops) {
    if (stop.ft < minFt || stop.ft > maxFt) continue;
    const y = yOf(stop.ft);
    parts.push(
      `<line x1="${PAD.left}" y1="${f(y)}" x2="${f(PAD.left + plotW)}" y2="${f(y)}" ` +
        `stroke="${stop.color}" stroke-opacity="0.22" stroke-width="1" />`,
    );
    parts.push(
      `<text x="${PAD.left - 6}" y="${f(y + 3.5)}" text-anchor="end" ` +
        `font-size="10" fill="currentColor" fill-opacity="0.65">${stop.ft}</text>`,
    );
  }
  parts.push(
    `<text x="4" y="${f(PAD.top - 5)}" font-size="9" fill="currentColor" fill-opacity="0.5">ft</text>`,
  );

  // --- 経過時間の目盛り（分） ---
  const totalMin = spanSec / 60;
  // 目盛りが混みすぎないよう、1/2/5/10分から選ぶ
  const stepMin = [1, 2, 5, 10, 15, 30].find((m) => totalMin / m <= 12) ?? 60;
  for (let m = 0; m * 60 + fromSec <= toSec; m += stepMin) {
    const sec = fromSec + m * 60;
    const x = xOf(sec);
    parts.push(
      `<line x1="${f(x)}" y1="${PAD.top}" x2="${f(x)}" y2="${f(PAD.top + plotH)}" ` +
        `stroke="currentColor" stroke-opacity="0.1" stroke-width="1" />`,
    );
    parts.push(
      `<text x="${f(x)}" y="${f(PAD.top + plotH + 15)}" text-anchor="middle" ` +
        `font-size="10" fill="currentColor" fill-opacity="0.65">${m}</text>`,
    );
  }
  parts.push(
    `<text x="${f(PAD.left + plotW)}" y="${f(PAD.top + plotH + 28)}" text-anchor="end" ` +
      `font-size="9" fill="currentColor" fill-opacity="0.5">分（電子マークから）</text>`,
  );

  // --- 帯（この画面の主役）---
  // 外側B（±outer）を描いてから内側A（±inner）を上に重ねる。参考図と同じ見え方に
  // なり、かつ「濃い＝2倍で有利」が直感的に分かる。
  parts.push(bandPolygon(task, task.outerBandFt, xOf, yOf, BAND_OUTER_COLOR, 'apt-band-outer'));
  parts.push(bandPolygon(task, task.innerBandFt, xOf, yOf, BAND_INNER_COLOR, 'apt-band-inner'));

  // --- 目標プロファイルの中心線 ---
  const centerD = task.profile
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${f(xOf(p.tSec))} ${f(yOf(p.altFt))}`)
    .join(' ');
  parts.push(
    `<path class="apt-profile" d="${centerD}" fill="none" stroke="${PROFILE_COLOR}" ` +
      `stroke-width="2" stroke-dasharray="5 3" stroke-linejoin="round" />`,
  );

  // --- 実測の高度（機体ごと）---
  let drawn = 0;
  if (startMs != null) {
    for (const tr of tracks) {
      const stroke = colorForTerminal(tr.terminalId);
      // プロファイルの時間範囲に入っている連続区間ごとに1本にする
      let run: string[] = [];
      const flush = (): void => {
        if (run.length >= 2) {
          parts.push(
            `<path class="apt-track" d="${run.join(' ')}" fill="none" stroke="${stroke}" ` +
              `stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />`,
          );
        }
        run = [];
      };
      for (const pt of tr.points) {
        const sec = (pt.t - startMs) / 1000;
        if (sec < fromSec || sec > toSec) {
          flush();
          continue;
        }
        const ft = metersToFeet(pt.alt);
        run.push(`${run.length === 0 ? 'M' : 'L'} ${f(xOf(sec))} ${f(yOf(ft))}`);
        drawn++;
      }
      flush();
    }
  }

  // --- 「いま」の縦線 ---
  if (startMs != null && nowMs != null) {
    const nowSec = (nowMs - startMs) / 1000;
    if (nowSec >= fromSec && nowSec <= toSec) {
      const x = xOf(nowSec);
      parts.push(
        `<line class="apt-now" x1="${f(x)}" y1="${PAD.top}" x2="${f(x)}" y2="${f(PAD.top + plotH)}" ` +
          `stroke="#ff9f1c" stroke-width="1.5" stroke-opacity="0.9" />`,
      );
    }
  }

  if (startMs == null) {
    parts.push(
      `<text x="${f(width / 2)}" y="${f(PAD.top + 16)}" text-anchor="middle" ` +
        `font-size="11" fill="currentColor" fill-opacity="0.6">` +
        `タスク開始（電子マーク）が未設定のため、航跡を重ねられません</text>`,
    );
  } else if (drawn === 0) {
    parts.push(
      `<text x="${f(width / 2)}" y="${f(PAD.top + 16)}" text-anchor="middle" ` +
        `font-size="11" fill="currentColor" fill-opacity="0.6">` +
        `この時間帯の航跡がまだありません</text>`,
    );
  }

  parts.push('</svg>');
  return parts.join('');
}

/**
 * 目標プロファイルの上下 halfFt の帯を1つの多角形にする。
 * 上側を左→右、下側を右→左でなぞって閉じる。
 */
function bandPolygon(
  task: AptTask,
  halfFt: number,
  xOf: (sec: number) => number,
  yOf: (ft: number) => number,
  color: string,
  cls: string,
): string {
  const f = (n: number) => n.toFixed(1);
  const upper = task.profile.map((p) => `${f(xOf(p.tSec))},${f(yOf(p.altFt + halfFt))}`);
  const lower = [...task.profile]
    .reverse()
    .map((p) => `${f(xOf(p.tSec))},${f(yOf(p.altFt - halfFt))}`);
  return (
    `<polygon class="${cls}" points="${[...upper, ...lower].join(' ')}" ` +
    `fill="${color}" fill-opacity="0.55" />`
  );
}

/**
 * 累積時間（秒）を「12分34秒」の形にする。
 * 重み付きの累積は実時間より長くなりうる（内側帯が2倍のため）ので、
 * 「分:秒」ではなく単位付きで出して勘違いを防ぐ。
 */
export function formatAccumulatedSec(sec: number): string {
  const total = Math.max(0, Math.round(sec));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}分${String(s).padStart(2, '0')}秒` : `${s}秒`;
}
